import { CardElementData } from '@adyen/adyen-web/dist/types/components/Card/types';
import { PaymentAction } from '@adyen/adyen-web/dist/types/types';
import { Env, OurContext } from '../context';
import {
  CommitResponseData,
  CreateActionResultResponseData,
  CreatePaymentResponseData,
} from '../generated-sources/payments-api';
import {
  getParameterByName,
  networkPollingAndRetry,
  OptionalErrorResponseTuple,
  PARAMS,
} from './util';
import AdyenLibDetails from '@adyen/adyen-web/package.json';
import { ERRORS } from './errors';

export async function createPayment(
  context: OurContext,
  cardData: CardElementData
): Promise<OptionalErrorResponseTuple<PaymentAction | null>> {
  try {
    const [err, response] = await networkPollingAndRetry(() =>
      context.cardEnrollmentApi.createPayment({
        paymentData: { adyenPaymentMethod: cardData.paymentMethod },
        authorization: `Bearer ${context.peApiAuth}`,
        ocpApimSubscriptionKey: context.peApiSubscriptionId,
        adyenSDKVersion: AdyenLibDetails.version,
      })
    );
    if (err) {
      return [new Error(ERRORS.NETWORK_ISSUE), null];
    }
    if (response?.adyenData?.resultCode?.toLowerCase?.() === 'refused') {
      return [new Error(ERRORS.REFUSED), null];
    }
    if (response?.adyenData?.resultCode?.toLowerCase?.() === 'authorised') {
      if (
        context.env === Env.UAT &&
        response?.testData?.interceptUrl &&
        response?.testData?.interceptUrl !== ''
      ) {
        window.location.replace(response.testData.interceptUrl);
        return [null, null];
      }
      return [new Error(ERRORS.UNEXPECTED_AUTHORISATION), null];
    }

    if (!response?.adyenData?.action) {
      return [new Error(ERRORS.NETWORK_ISSUE), null];
    }
    return [null, response.adyenData.action as PaymentAction];
  } catch (e) {
    return [e as Error, null];
  }
}

export async function completePayment(
  context: OurContext,
  actionResult: Record<string, unknown>
): Promise<OptionalErrorResponseTuple<CreateActionResultResponseData>> {
  try {
    const [err, response] = await networkPollingAndRetry(() =>
      context.cardEnrollmentApi.createActionResult({
        actionResult: { ...actionResult },
        authorization: `Bearer ${context.peApiAuth}`,
        ocpApimSubscriptionKey: context.peApiSubscriptionId,
        adyenSDKVersion: AdyenLibDetails.version,
      })
    );
    if (err || !response) {
      return [err || new Error(ERRORS.NETWORK_ISSUE), null];
    }
    return [null, response];
  } catch (e) {
    return [e as Error, null];
  }
}

export async function initiateFSCP(
  context: OurContext,
  cardData: CardElementData,
  peToken: string
): Promise<OptionalErrorResponseTuple<CreatePaymentResponseData>> {
  try {
    const [err, response] = await networkPollingAndRetry(() =>
      context.fscpApi.initiateFSCPayment({
        paymentData: {
          adyenPaymentMethod: cardData.paymentMethod,
          returnURL: `${context.host}/checkout/auth?${PARAMS.CARD_PAGE_TOKEN}=${context.epaymentsApiAuth}`,
          origin: cardData.origin,
          browserInfo: cardData.browserInfo,
          seed: context.epaymentsApiAuth,
        },
        authorization: `Bearer ${peToken}`,
        ocpApimSubscriptionKey: context.peApiSubscriptionId,
        adyenSDKVersion: AdyenLibDetails.version,
      })
    );
    if (err || !response) {
      return [new Error(ERRORS.NETWORK_ISSUE), null];
    }
    if (response?.adyenData?.resultCode?.toLowerCase?.() === 'refused') {
      return [new Error(ERRORS.REFUSED), null];
    }
    if (response?.adyenData?.resultCode?.toLowerCase?.() === 'authorised') {
      return [new Error(ERRORS.UNEXPECTED_AUTHORISATION), null];
    }

    return [null, response];
  } catch (e) {
    return [e as Error, null];
  }
}

export async function commitFSCP(
  context: OurContext
): Promise<OptionalErrorResponseTuple<CommitResponseData>> {
  try {
    const token = getParameterByName(PARAMS.PAYMENT_TOKEN);
    if (token == null) {
      return [new Error(ERRORS.INVALID_INPUT), null];
    }

    const [err, response] = await networkPollingAndRetry(() =>
      context.fscpApi.commitFSCPayment({
        authorization: `Bearer ${token}`,
        ocpApimSubscriptionKey: context.peApiSubscriptionId,
        adyenSDKVersion: AdyenLibDetails.version,
      })
    );
    if (err || !response) {
      return [err || new Error(ERRORS.NETWORK_ISSUE), null];
    }
    return [null, response];
  } catch (e) {
    return [e as Error, null];
  }
}
